import { useState } from 'react';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  CONTENT_STATE_CATEGORIES, DEADLINE_RULES, DEADLINE_RULE_LABELS, FIELD_TYPES, FIELD_TYPE_LABELS, PLATFORMS, PLATFORM_LABELS,
  STATE_CATEGORY_LABELS, WORKSPACE_COLORS, type EditorialConfig, type EditorialSeries, type SeriesInput, type Workspace,
} from '@unitly/contracts';
import { Sheet } from '../../ui/Sheet.tsx';
import { SelectField, TextField, wsColor } from '../../ui/primitives.tsx';
import { chipColor } from '../../ui/MonthGrid.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import { useEditorialSettings, useMembers } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { WEEKDAY_ORDER, WEEKDAY_SHORT } from '../../lib/calendar.ts';

type Tab = 'series' | 'states' | 'formats' | 'channels' | 'fields' | 'deadline';
const TABS: { id: Tab; label: string }[] = [
  { id: 'series', label: 'Palinsesto' },
  { id: 'states', label: 'Stati' },
  { id: 'formats', label: 'Formati' },
  { id: 'channels', label: 'Canali' },
  { id: 'fields', label: 'Campi' },
  { id: 'deadline', label: 'Scadenza' },
];

export function PedSettingsSheet({ open, onClose, workspace, config }: { open: boolean; onClose: () => void; workspace: Workspace; config: EditorialConfig }) {
  const [tab, setTab] = useState<Tab>('series');
  return (
    <Sheet open={open} onClose={onClose} full title={`PED di ${workspace.name}`} labelledBy="ped-settings-title"
      headerActions={<button type="button" className="btn btn-plain btn-sm" style={{ fontWeight: 600 }} onClick={onClose}>Fine</button>}>
      <div className="settings-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" className="chip" aria-pressed={tab === t.id} aria-selected={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>
      {tab === 'series' && <SeriesTab config={config} workspaceId={workspace.id} />}
      {tab === 'states' && <StatesTab config={config} workspaceId={workspace.id} />}
      {tab === 'formats' && <FormatsTab config={config} workspaceId={workspace.id} />}
      {tab === 'channels' && <ChannelsTab config={config} workspaceId={workspace.id} />}
      {tab === 'fields' && <FieldsTab config={config} workspaceId={workspace.id} />}
      {tab === 'deadline' && <DeadlineTab config={config} workspaceId={workspace.id} />}
    </Sheet>
  );
}

function useSettings(workspaceId: string) {
  const { orgId } = useOrgCtx();
  const m = useEditorialSettings(orgId, workspaceId);
  const toast = useToast();
  const call = (path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown, ok?: string, done?: () => void) =>
    m.mutate({ path, method, body }, {
      onSuccess: () => { if (ok) toast(ok); done?.(); },
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Modifica non salvata'),
    });
  return { call, pending: m.isPending, error: m.error instanceof ApiError ? m.error : null };
}

// ------------------------------------------------------------------ palinsesto
function SeriesTab({ config, workspaceId }: { config: EditorialConfig; workspaceId: string }) {
  const [editing, setEditing] = useState<EditorialSeries | 'new' | null>(null);
  const { call } = useSettings(workspaceId);
  if (editing) return <SeriesForm config={config} workspaceId={workspaceId} series={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />;
  return (
    <>
      <p className="hint" style={{ marginTop: 0 }}>
        Le rubriche e i loro giorni di uscita. “Genera dal palinsesto” crea le uscite di tutto il mese partendo da qui, con formato, canali, ora e responsabile già compilati.
      </p>
      <div className="card list">
        {config.series.map((s) => (
          <div key={s.id} className="list-row series-row">
            <span className="grow">
              <div style={{ fontWeight: 600 }}>{s.name}</div>
              <div className="secondary" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                <span className="days">
                  {WEEKDAY_ORDER.map((d, i) => (
                    <i key={d} className={s.weekdays.includes(d) ? 'on' : ''} style={{ '--sc': chipColor(s.color) } as React.CSSProperties}>{WEEKDAY_SHORT[i]}</i>
                  ))}
                </span>
                {s.defaultTime && <span>{s.defaultTime}</span>}
                {s.defaultFormat && <span>{s.defaultFormat}</span>}
              </div>
            </span>
            <button type="button" className="icon-btn muted" aria-label={`Modifica ${s.name}`} onClick={() => setEditing(s)}><Pencil size={17} /></button>
            <button type="button" className="icon-btn muted" aria-label={`Elimina ${s.name}`}
              onClick={() => confirm(`Eliminare la rubrica "${s.name}"? I contenuti già creati restano nel PED.`) && call(`/series/${s.id}`, 'DELETE', undefined, 'Rubrica eliminata')}>
              <Trash2 size={17} />
            </button>
          </div>
        ))}
        {!config.series.length && <p className="hint" style={{ padding: 16, margin: 0 }}>Nessuna rubrica. Esempio da STRTWR: “What If” il martedì e il venerdì, “Release Radar” la domenica.</p>}
      </div>
      <button type="button" className="btn btn-secondary" style={{ marginTop: 14 }} onClick={() => setEditing('new')}><Plus size={17} /> Nuova rubrica</button>
    </>
  );
}

function SeriesForm({ config, workspaceId, series, onDone }: { config: EditorialConfig; workspaceId: string; series: EditorialSeries | null; onDone: () => void }) {
  const { orgId } = useOrgCtx();
  const members = useMembers(orgId).data ?? [];
  const { call, pending, error } = useSettings(workspaceId);
  const [v, setV] = useState<Required<Omit<SeriesInput, 'description'>> & { description: string }>({
    name: series?.name ?? '', description: series?.description ?? '', color: series?.color ?? 'indigo', weekdays: series?.weekdays ?? [],
    defaultTime: series?.defaultTime ?? null, defaultFormat: series?.defaultFormat ?? null, defaultChannelIds: series?.defaultChannelIds ?? [],
    ownerUserId: series?.ownerUserId ?? null,
  });
  const toggle = <T,>(arr: T[], x: T) => (arr.includes(x) ? arr.filter((a) => a !== x) : [...arr, x]);
  return (
    <form className="form" onSubmit={(e) => {
      e.preventDefault();
      const body = { ...v, description: v.description || null };
      call(series ? `/series/${series.id}` : '/series', series ? 'PATCH' : 'POST', body, series ? 'Rubrica aggiornata' : 'Rubrica creata', onDone);
    }}>
      <TextField data-autofocus label="Nome della rubrica" placeholder="Es. What If, Release Radar, Drinkanza" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} error={error?.errors.name} required maxLength={80} />
      <div className="field">
        <span>Giorni di uscita</span>
        <div className="weekday-picker" role="group" aria-label="Giorni di uscita">
          {WEEKDAY_ORDER.map((d, i) => (
            <button key={d} type="button" aria-pressed={v.weekdays.includes(d)} aria-label={['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica'][i]}
              onClick={() => setV({ ...v, weekdays: toggle(v.weekdays, d) })}>{WEEKDAY_SHORT[i]}</button>
          ))}
        </div>
      </div>
      <div className="inline-form">
        <label className="field"><span>Ora</span><input className="input" type="time" value={v.defaultTime ?? ''} onChange={(e) => setV({ ...v, defaultTime: e.target.value || null })} /></label>
        <SelectField label="Formato" value={v.defaultFormat ?? ''} onChange={(e) => setV({ ...v, defaultFormat: e.target.value || null })}>
          <option value="">—</option>{config.formats.map((f) => <option key={f} value={f}>{f}</option>)}
        </SelectField>
      </div>
      <SelectField label="Responsabile" value={v.ownerUserId ?? ''} onChange={(e) => setV({ ...v, ownerUserId: e.target.value || null })}>
        <option value="">Nessuno</option>{members.map((m) => <option key={m.userId} value={m.userId}>{m.displayName}</option>)}
      </SelectField>
      <div className="field">
        <span>Canali</span>
        <div className="people-picker">
          {config.channels.map((c) => (
            <button key={c.id} type="button" className="channel-chip" aria-pressed={v.defaultChannelIds.includes(c.id)} onClick={() => setV({ ...v, defaultChannelIds: toggle(v.defaultChannelIds, c.id) })}>
              {PLATFORM_LABELS[c.platform]} <span style={{ opacity: 0.7 }}>{c.handle}</span>
            </button>
          ))}
          {!config.channels.length && <span className="hint">Aggiungi prima i canali nella scheda “Canali”.</span>}
        </div>
      </div>
      <div className="field">
        <span>Colore</span>
        <div className="color-picker">
          {WORKSPACE_COLORS.map((c) => <button key={c} type="button" className="color-swatch" aria-label={c} aria-pressed={v.color === c} style={{ '--sw': wsColor(c) } as React.CSSProperties} onClick={() => setV({ ...v, color: c })} />)}
        </div>
      </div>
      <label className="field"><span>Descrizione</span><textarea className="textarea" rows={2} placeholder="Di cosa parla, tono, riferimenti" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} /></label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-secondary" onClick={onDone}>Annulla</button>
        <button className="btn btn-primary" style={{ flex: 1 }} disabled={pending || !v.name.trim()}>{series ? 'Salva' : 'Crea rubrica'}</button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------------ stati
function StatesTab({ config, workspaceId }: { config: EditorialConfig; workspaceId: string }) {
  const { call } = useSettings(workspaceId);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<(typeof CONTENT_STATE_CATEGORIES)[number]>('in_progress');
  const move = (i: number, dir: -1 | 1) => {
    const ids = config.states.map((s) => s.id);
    const j = i + dir;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    call('/states/reorder', 'POST', { ids });
  };
  return (
    <>
      <p className="hint" style={{ marginTop: 0 }}>Gli stati attivano la regola della revisione: un contenuto entra negli stati con “Revisione PM” solo dopo essere stato revisionato.</p>
      <div className="card list">
        {config.states.map((s, i) => (
          <div key={s.id} className="list-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="state-dot" style={{ '--sd': chipColor(s.color) } as React.CSSProperties} />
            <input className="input" style={{ flex: '1 1 140px', minHeight: 34, padding: '4px 10px' }} defaultValue={s.name} aria-label="Nome stato"
              onBlur={(e) => e.target.value.trim() && e.target.value !== s.name && call(`/states/${s.id}`, 'PATCH', { name: e.target.value.trim() })} />
            <select className="select" style={{ width: 'auto', minHeight: 34, padding: '4px 28px 4px 10px', fontSize: 14 }} value={s.category} aria-label="Tipo"
              onChange={(e) => call(`/states/${s.id}`, 'PATCH', { category: e.target.value })}>
              {CONTENT_STATE_CATEGORIES.map((c) => <option key={c} value={c}>{STATE_CATEGORY_LABELS[c]}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--label-2)' }}>
              <button type="button" role="switch" className="switch" aria-checked={s.requiresApproval} aria-label={`Revisione PM richiesta per ${s.name}`}
                onClick={() => call(`/states/${s.id}`, 'PATCH', { requiresApproval: !s.requiresApproval })} />
              Revisione PM
            </label>
            <span style={{ display: 'flex' }}>
              <button type="button" className="icon-btn muted" aria-label="Sposta su" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={16} /></button>
              <button type="button" className="icon-btn muted" aria-label="Sposta giù" disabled={i === config.states.length - 1} onClick={() => move(i, 1)}><ArrowDown size={16} /></button>
              <button type="button" className="icon-btn muted" aria-label={`Elimina ${s.name}`} onClick={() => call(`/states/${s.id}`, 'DELETE', undefined, 'Stato eliminato')}><Trash2 size={16} /></button>
            </span>
            {s.itemCount > 0 && <span className="hint" style={{ width: '100%', padding: '0 0 0 18px' }}>{s.itemCount} contenuti</span>}
          </div>
        ))}
      </div>
      <form className="inline-form" style={{ marginTop: 14 }} onSubmit={(e) => { e.preventDefault(); call('/states', 'POST', { name, category, requiresApproval: false }, 'Stato aggiunto', () => setName('')); }}>
        <TextField label="Nuovo stato" placeholder="Es. Shooting" value={name} onChange={(e) => setName(e.target.value)} />
        <SelectField label="Tipo" value={category} onChange={(e) => setCategory(e.target.value as typeof category)}>
          {CONTENT_STATE_CATEGORIES.map((c) => <option key={c} value={c}>{STATE_CATEGORY_LABELS[c]}</option>)}
        </SelectField>
        <button className="btn btn-secondary" disabled={!name.trim()}><Plus size={16} /> Aggiungi</button>
      </form>
    </>
  );
}

// ------------------------------------------------------------------ formati
function FormatsTab({ config, workspaceId }: { config: EditorialConfig; workspaceId: string }) {
  const { call } = useSettings(workspaceId);
  const [value, setValue] = useState('');
  const save = (formats: string[], ok?: string) => call('', 'PATCH', { formats }, ok);
  return (
    <>
      <p className="hint" style={{ marginTop: 0 }}>I formati tra cui scegliere in ogni contenuto di questo media.</p>
      <div className="people-picker" style={{ marginBottom: 14 }}>
        {config.formats.map((f) => (
          <span key={f} className="channel-chip">{f}
            <button type="button" aria-label={`Rimuovi ${f}`} style={{ display: 'grid', color: 'var(--label-3)' }} onClick={() => save(config.formats.filter((x) => x !== f))}><X size={14} /></button>
          </span>
        ))}
      </div>
      <form className="inline-form" onSubmit={(e) => { e.preventDefault(); if (value.trim()) save([...config.formats, value.trim()], 'Formato aggiunto'); setValue(''); }}>
        <TextField label="Nuovo formato" placeholder="Es. Meme, Podcast clip, Newsletter" value={value} onChange={(e) => setValue(e.target.value)} maxLength={40} />
        <button className="btn btn-secondary" disabled={!value.trim()}><Plus size={16} /> Aggiungi</button>
      </form>
    </>
  );
}

// ------------------------------------------------------------------ canali
function ChannelsTab({ config, workspaceId }: { config: EditorialConfig; workspaceId: string }) {
  const { call, error } = useSettings(workspaceId);
  const [platform, setPlatform] = useState<(typeof PLATFORMS)[number]>('instagram');
  const [handle, setHandle] = useState('');
  return (
    <>
      <p className="hint" style={{ marginTop: 0 }}>Gli account del media. Più avanti gli stessi canali porteranno le statistiche (Instagram, TikTok, YouTube) direttamente nel PED.</p>
      <div className="card list">
        {config.channels.map((c) => (
          <div key={c.id} className="list-row">
            <span className="grow"><div>{PLATFORM_LABELS[c.platform]}</div><div className="secondary">{c.handle}</div></span>
            <button type="button" className="icon-btn muted" aria-label={`Rimuovi ${c.handle}`} onClick={() => call(`/channels/${c.id}`, 'DELETE', undefined, 'Canale rimosso')}><Trash2 size={17} /></button>
          </div>
        ))}
        {!config.channels.length && <p className="hint" style={{ padding: 16, margin: 0 }}>Nessun canale. Per Ragazze: Instagram @ragazze, TikTok @ragazze, Facebook, YouTube, Threads.</p>}
      </div>
      <form className="inline-form" style={{ marginTop: 14 }} onSubmit={(e) => { e.preventDefault(); call('/channels', 'POST', { platform, handle }, 'Canale aggiunto', () => setHandle('')); }}>
        <SelectField label="Piattaforma" value={platform} onChange={(e) => setPlatform(e.target.value as typeof platform)}>
          {PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABELS[p]}</option>)}
        </SelectField>
        <TextField label="Account" placeholder="@account o sito" value={handle} onChange={(e) => setHandle(e.target.value)} error={error?.errors.handle} />
        <button className="btn btn-secondary" disabled={!handle.trim()}><Plus size={16} /> Aggiungi</button>
      </form>
    </>
  );
}

// ------------------------------------------------------------------ campi personalizzati
function FieldsTab({ config, workspaceId }: { config: EditorialConfig; workspaceId: string }) {
  const { call, error } = useSettings(workspaceId);
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState<(typeof FIELD_TYPES)[number]>('text');
  const [options, setOptions] = useState('');
  const needsOptions = fieldType === 'select' || fieldType === 'multi_select';
  return (
    <>
      <p className="hint" style={{ marginTop: 0 }}>Proprietà in più per ogni contenuto, come le colonne di un database Notion: brand partner, hashtag, talent, budget…</p>
      <div className="card list">
        {config.fields.map((f) => (
          <div key={f.id} className="list-row">
            <span className="grow"><div>{f.label}</div><div className="secondary">{FIELD_TYPE_LABELS[f.fieldType]}{f.options.length ? ` · ${f.options.join(', ')}` : ''}</div></span>
            <button type="button" className="icon-btn muted" aria-label={`Elimina ${f.label}`}
              onClick={() => confirm(`Eliminare il campo "${f.label}" e i suoi valori da tutti i contenuti?`) && call(`/fields/${f.id}`, 'DELETE', undefined, 'Campo eliminato')}><Trash2 size={17} /></button>
          </div>
        ))}
        {!config.fields.length && <p className="hint" style={{ padding: 16, margin: 0 }}>Nessun campo personalizzato.</p>}
      </div>
      <form className="form" style={{ marginTop: 14 }} onSubmit={(e) => {
        e.preventDefault();
        call('/fields', 'POST', { label, fieldType, options: needsOptions ? options.split(',').map((o) => o.trim()).filter(Boolean) : [] }, 'Campo aggiunto', () => { setLabel(''); setOptions(''); });
      }}>
        <div className="inline-form">
          <TextField label="Nuovo campo" placeholder="Es. Brand partner" value={label} onChange={(e) => setLabel(e.target.value)} error={error?.errors.label} />
          <SelectField label="Tipo" value={fieldType} onChange={(e) => setFieldType(e.target.value as typeof fieldType)}>
            {FIELD_TYPES.map((t) => <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>)}
          </SelectField>
        </div>
        {needsOptions && <TextField label="Opzioni (separate da virgola)" placeholder="Polymarket, Tickly, Aurora Biofarma" value={options} onChange={(e) => setOptions(e.target.value)} error={error?.errors.options} />}
        <button className="btn btn-secondary" disabled={!label.trim()}><Plus size={16} /> Aggiungi campo</button>
      </form>
    </>
  );
}

// ------------------------------------------------------------------ scadenza
function DeadlineTab({ config, workspaceId }: { config: EditorialConfig; workspaceId: string }) {
  const { call } = useSettings(workspaceId);
  return (
    <div className="form">
      <p className="hint" style={{ marginTop: 0 }}>Entro quando il PM deve completare il piano del mese successivo. La scadenza compare in cima al PED insieme ai contenuti ancora da revisionare.</p>
      <div className="card list">
        {DEADLINE_RULES.map((r) => (
          <button key={r} type="button" className="list-row" onClick={() => call('', 'PATCH', { deadlineRule: r }, 'Scadenza aggiornata')}>
            <span className="grow">{DEADLINE_RULE_LABELS[r]}</span>
            {config.deadlineRule === r && <span className="dot" aria-label="Selezionata" />}
          </button>
        ))}
      </div>
    </div>
  );
}
